import { create } from 'zustand';
import type { SurveySummary, SurveyDetail, SurveyResults, SurveyView } from '../features/survey/types';
import { surveyApi } from '../services/api/survey';

const _inflight: Record<string, Promise<void>> = {};

interface SurveyState {
  view: SurveyView;
  surveys: SurveySummary[];
  activeSurvey: SurveyDetail | null;
  activeResults: SurveyResults | null;
  isLoading: boolean;
  error: string | null;
}

interface SurveyActions {
  setView: (view: SurveyView) => void;
  loadSurveys: (status?: string) => Promise<void>;
  loadSurvey: (id: string) => Promise<void>;
  loadResults: (id: string) => Promise<void>;
  createSurvey: (data: {
    title: string;
    questions: { question_text: string; position: number }[];
    recipient_emails?: string[];
    deadline?: string | null;
  }) => Promise<SurveyDetail>;
  updateSurvey: (id: string, data: {
    title?: string;
    questions?: { question_text: string; position: number }[];
    recipient_emails?: string[];
    deadline?: string | null;
  }) => Promise<SurveyDetail>;
  deleteSurvey: (id: string) => Promise<void>;
  sendSurvey: (id: string) => Promise<void>;
  stopSurvey: (id: string) => Promise<void>;
  setActiveSurvey: (survey: SurveyDetail | null) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const INITIAL_STATE: SurveyState = {
  view: 'board',
  surveys: [],
  activeSurvey: null,
  activeResults: null,
  isLoading: false,
  error: null,
};

export const useSurveyStore = create<SurveyState & SurveyActions>((set, get) => ({
  ...INITIAL_STATE,

  setView: (view) => set({ view }),

  loadSurveys: (status) => {
    const key = `surveys:${status ?? 'all'}`;
    if (_inflight[key]) return _inflight[key];
    _inflight[key] = (async () => {
      set({ isLoading: true, error: null });
      try {
        const { data } = await surveyApi.listSurveys(status);
        set({ surveys: data, isLoading: false });
      } catch (e: any) {
        set({ error: e.message, isLoading: false });
      } finally {
        delete _inflight[key];
      }
    })();
    return _inflight[key];
  },

  loadSurvey: (id) => {
    const key = `survey:${id}`;
    if (_inflight[key]) return _inflight[key];
    _inflight[key] = (async () => {
      set({ isLoading: true, error: null });
      try {
        const { data } = await surveyApi.getSurvey(id);
        set({ activeSurvey: data, isLoading: false });
      } catch (e: any) {
        set({ error: e.message, isLoading: false });
      } finally {
        delete _inflight[key];
      }
    })();
    return _inflight[key];
  },

  loadResults: (id) => {
    const key = `results:${id}`;
    if (_inflight[key]) return _inflight[key];
    _inflight[key] = (async () => {
      set({ isLoading: true, error: null });
      try {
        const { data } = await surveyApi.getResults(id);
        set({ activeResults: data, isLoading: false });
      } catch (e: any) {
        set({ error: e.message, isLoading: false });
      } finally {
        delete _inflight[key];
      }
    })();
    return _inflight[key];
  },

  createSurvey: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const { data: survey } = await surveyApi.createSurvey(data);
      set((s) => ({
        surveys: [
          {
            id: survey.id,
            title: survey.title,
            status: survey.status,
            deadline: survey.deadline,
            created_by: survey.created_by,
            creator_name: survey.creator_name,
            recipient_count: survey.recipients.length,
            response_count: 0,
            question_count: survey.questions.length,
            created_at: survey.created_at,
            updated_at: survey.updated_at,
          },
          ...s.surveys,
        ],
        activeSurvey: survey,
        isLoading: false,
      }));
      return survey;
    } catch (e: any) {
      set({ error: e.message, isLoading: false });
      throw e;
    }
  },

  updateSurvey: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const { data: survey } = await surveyApi.updateSurvey(id, data);
      set({ activeSurvey: survey, isLoading: false });
      return survey;
    } catch (e: any) {
      set({ error: e.message, isLoading: false });
      throw e;
    }
  },

  deleteSurvey: async (id) => {
    try {
      await surveyApi.deleteSurvey(id);
      set((s) => {
        const clearingActive = s.activeSurvey?.id === id;
        const clearingResults = s.activeResults?.survey_id === id;
        return {
          surveys: s.surveys.filter((sv) => sv.id !== id),
          activeSurvey: clearingActive ? null : s.activeSurvey,
          activeResults: clearingResults ? null : s.activeResults,
          view: clearingActive || clearingResults ? 'board' : s.view,
        };
      });
    } catch (e: any) {
      set({ error: e.message });
    }
  },

  sendSurvey: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const { data: survey } = await surveyApi.sendSurvey(id);
      set((s) => ({
        surveys: s.surveys.map((sv) => (sv.id === id ? { ...sv, status: survey.status } : sv)),
        activeSurvey: survey,
        isLoading: false,
      }));
    } catch (e: any) {
      set({ error: e.message, isLoading: false });
    }
  },

  stopSurvey: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const { data: survey } = await surveyApi.stopSurvey(id);
      set((s) => ({
        surveys: s.surveys.map((sv) => (sv.id === id ? { ...sv, status: survey.status } : sv)),
        activeSurvey: survey,
        isLoading: false,
      }));
    } catch (e: any) {
      set({ error: e.message, isLoading: false });
    }
  },

  setActiveSurvey: (survey) => set({ activeSurvey: survey }),
  setError: (error) => set({ error }),
  reset: () => set(INITIAL_STATE),
}));
